import { Quiz } from "../../types/quiz.types";
import {
  OnboardingStep,
  OnboardingStepName,
} from "../../types/onboarding.types";
import { Program, Invitation } from "../../types/platform.types";
import { makeContext } from "@shared/utils/makeContext";

// --- Name ---
const NAME = "Onboarding";

// --- Types ---
export type OnboardingState = {
  organization?: any;
  quizes?: Quiz[];
  steps: OnboardingStep[];
  invitation?: Invitation | null;
  program?: Program | null;
  isLoading?: boolean;
};

export type OnboardingAction = {
  type: string;
  payload: any;
};

// --- Constants ---
export const STEP_COMPLETED = "step/completed" as const;
export const ONBOARDING_MOUNT = "onboarding/mount" as const;

export const ACTION_TYPES = {
  STEP_COMPLETED,
  ONBOARDING_MOUNT,
} as const;

// --- Initial State ---
export const initialOnboarding: OnboardingState = {
  organization: undefined,
  steps: [
    { name: "intro", isActive: true },
    { name: "fit", isWithQuiz: true },
    { name: "signup" },
    { name: "matching-intro" },
    { name: "matching", isWithQuiz: true },
    { name: "invite-intro" },
    { name: "invite" },
    { name: "complete" },
  ],
  invitation: undefined,
  program: undefined,
  isLoading: true,
};

// --- Reducer ---
function onboardingReducer(
  state: OnboardingState,
  action: OnboardingAction
): OnboardingState {
  switch (action.type) {
    case STEP_COMPLETED: {
      const { stepName } = action.payload as { stepName: OnboardingStepName };

      const currentIndex = state.steps.findIndex(
        (step) => step.name === stepName
      );

      return {
        ...state,
        steps: state.steps.map((step, index) => {
          if (index < currentIndex) {
            return { ...step, isCompleted: true, isActive: false };
          }

          if (index === currentIndex) {
            return { ...step, isCompleted: true, isActive: false };
          }

          if (index === currentIndex + 1) {
            return { ...step, isActive: true };
          }

          return step;
        }),
      };
    }

    case ONBOARDING_MOUNT: {
      return {
        ...state,
        ...action.payload,
        isLoading: false,
      };
    }

    default:
      throw new Error("Unknown action: " + action.type);
  }
}

// --- Context Creation ---
export const {
  Provider: OnboardingProvider,
  useState: useOnboarding,
  useDispatch: useOnboardingDispatch,
} = makeContext<OnboardingState, OnboardingAction>({
  name: NAME,
  initialState: initialOnboarding,
  reducer: onboardingReducer,
});
